import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { ZodIssue } from 'zod';
import { ZodError, z } from 'zod';

export const createOrderSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: z.string().min(12, VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE()),
      address: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      street_number: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      city: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      postcode: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      country: z.object({ id: z.string().min(1).or(z.number()) }),

      company_buy: z.boolean().default(false),
      // if company_buy is true
      company: z.string().optional(),
      ico: z.string().optional(),
      dic: z.string().optional(),
      ic_dph: z.string().optional(),

      different_delivery_address: z.boolean().default(false),
      hide_different_delivery_address: z.boolean().default(false),
      region: z.any().optional().nullable(),

      address2: z.string().optional().nullable(),
      street_number2: z.string().optional().nullable(),
      city2: z.string().optional().nullable(),
      postcode2: z.string().optional().nullable(),
      country2: z.object({ id: z.any().optional() }).optional().nullable(),

      gdpr: z
        .boolean()
        .default(false)
        .refine(
          value => value === true,
          VM.TOS_VALIDATION_MESSAGE() + '. ' + VM.GDPR_VALIDATION_MESSAGE(),
        ),
      // must be true
      // conditions: z
      //   .boolean()
      //   .default(false)
      //   .refine(value => value === true),
      newsletter: z.boolean().default(false).optional(),
      create_account: z.boolean().default(false).optional(),

      company_name: z.string().optional(),
      iban: z.string().optional(),

      password: z.string().optional(),
      password_confirmation: z.string().optional(),

      note: z.string().optional(),

      return_url: z.string().optional(),
      newsletter_return_url: z.string(),
      check_status_url: z.string(),

      is_wholesaler: z.boolean().optional(),
      wholesaler_order_type: z.string().default('storage'),

      dropship_name: z.string().optional(),
      dropship_surname: z.string().optional(),
      dropship_email: z.string().optional(),
      dropship_phone: z.string().optional(),
      dropship_address: z.string().optional(),
      dropship_street_number: z.string().optional(),
      dropship_city: z.string().optional(),
      dropship_postcode: z.string().optional(),
      dropship_country: z.object({ id: z.string().or(z.number()) }).optional(),

      send_heureka_mail: z.boolean().optional(),
    })
    .refine(
      data => {
        const errors: ZodIssue[] = [];
        if (data.wholesaler_order_type === 'dropship') {
          if (!data.dropship_name || data.dropship_name.length === 0) {
            errors.push({
              path: ['dropship_name'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_surname || data.dropship_surname.length === 0) {
            errors.push({
              path: ['dropship_surname'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_email || data.dropship_email.length === 0) {
            errors.push({
              path: ['dropship_email'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_phone || data.dropship_phone.length < 5) {
            errors.push({
              path: ['dropship_phone'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_address || data.dropship_address.length === 0) {
            errors.push({
              path: ['dropship_address'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_street_number || data.dropship_street_number.length === 0) {
            errors.push({
              path: ['dropship_street_number'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_city || data.dropship_city.length === 0) {
            errors.push({
              path: ['dropship_city'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_postcode || data.dropship_postcode.length === 0) {
            errors.push({
              path: ['dropship_postcode'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_country || data.dropship_country.id.toString().length === 0) {
            errors.push({
              path: ['dropship_country.id'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.company_name || data.company_name.length === 0) {
            errors.push({
              path: ['company_name'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (data.different_delivery_address) {
          if (!data.address2 || data.address2.length === 0) {
            errors.push({
              path: ['address2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.street_number2 || data.street_number2.length === 0) {
            errors.push({
              path: ['street_number2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.city2 || data.city2.length === 0) {
            errors.push({
              path: ['city2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.postcode2 || data.postcode2.length === 0) {
            errors.push({
              path: ['postcode2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.country2 || data.country2.id?.toString().length === 0) {
            errors.push({
              path: ['country2.id'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (data.company_buy) {
          if (!data.company?.trim()) {
            errors.push({
              path: ['company'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.ico || data.ico.length === 0) {
            errors.push({
              path: ['ico'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (errors.length > 0) {
          throw new ZodError(errors);
        }

        return true;
      },
      {
        path: ['wholesaler_order_type'],
      },
    );

export type CreateOrderFormValues = z.infer<ReturnType<typeof createOrderSchema>>;
